import React from "react";
import { UseFormReturn } from "react-hook-form";

import { 
    Accordion, 
    AccordionDetails, 
    AccordionSummary, 
    Button, 
    Fab, 
    FormControl, 
    Grid, 
    IconButton, 
    List, 
    ListItem, 
    ListItemIcon, 
    ListItemText, 
    Stack, 
    Tooltip, 
    Typography, 
} from "@mui/material";

import { t, Trans } from "@lingui/macro";

import { LanguageAndSubtitlesDataProps, LanguageAndSubtitlesDataState } from "./models";

import { ZPlayAutocomplete } from "commons/fields";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ClearIcon from "@mui/icons-material/Clear";
import CircleIcon from '@mui/icons-material/Circle';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorIcon from '@mui/icons-material/Error';
import HelpIcon from "@mui/icons-material/Help";

export class LanguageAndSubtitlesData extends React.Component<LanguageAndSubtitlesDataProps & UseFormReturn, LanguageAndSubtitlesDataState>
{

    constructor(props: any) {

        super(props);

        this.state = {
            languages: this.props.languageList,
            fileSubtitles: null,
            fileSubtitlesName: null,
            isSubtitlesUploaded: false,
            error: false,
        };
    }

    private parseFileSubtitles = (event: any) => {
        this.setState(() => ({
            fileSubtitles: event.target.files[0],
            fileSubtitlesName: event.target.files[0].name,
            isSubtitlesUploaded: true,
        }));
    };

    private removeFileSubtitles = () => {
        this.setState(() => ({
            fileSubtitles: null,
            fileSubtitlesName: null,
            isSubtitlesUploaded: false,
        }));
    };

    private addSubtitles = () => {

        let isoCode = this.props.languageList.find(language => language.id ===  this.props.getValues(`${this.props.name}.subtitlesLanguage`))?.isoCode

        let subtitles = {
            language: isoCode ? isoCode : "",
            fileName: this.state.fileSubtitlesName,
            file: this.state.fileSubtitles
        }

        if (subtitles.language==="" || !this.state.isSubtitlesUploaded) {
            this.setState(() => ({ error: true }));
            return
        }

        let updatedSubtitlesList = this.props.subtitles.concat(subtitles);

        this.props.updateSubtitles(updatedSubtitlesList);

        this.setState(() => ({
            isSubtitlesUploaded: false,
            error: false,
        }));
    }

    private delete = (language: string) => {
        let updatedSubtitlesList = this.props.subtitles.filter(item => item.language !== language);
        this.props.updateSubtitles(updatedSubtitlesList);
    }

    render() {
        return (
            <Accordion sx={{ backgroundColor: process.env.REACT_APP_THIRD_COLOR }}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: "white" }} />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <Typography color={process.env.REACT_APP_DEFAULT_TEXT_COLOR}>
                        <Trans id={"lasd-language-subtitle-section"}>Lingua e sottotitoli</Trans>
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container item xs={12} md={4} mb={3}>
                        <ZPlayAutocomplete
                            fieldName={`${this.props.name}.language`}
                            label={t({
                                id: "lasd-language",
                                message: "Lingua originale"
                            })}
                            formState={this.props.formState}
                            control={this.props.control}
                            trigger={this.props.trigger}
                            data={this.state.languages}/>
                    </Grid>
                    {this.props.subtitles.length > 0 && <Grid container mb={2}>
                        <Grid item xs={12} md={8} color={process.env.REACT_APP_DEFAULT_TEXT_COLOR}>
                            <List dense>
                                {this.props.subtitles.map((item, i) =>
                                    <ListItem
                                        key={i}
                                        secondaryAction={
                                            <IconButton edge="end" aria-label="delete" onClick={() => this.delete(item.language)}>
                                                <DeleteIcon fontSize="small" sx={{ color: process.env.REACT_APP_DEFAULT_TEXT_COLOR }} />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemIcon sx={{ minWidth: "35px" }}>
                                            <CircleIcon sx={{ width: "10px", height: "10px", color: process.env.REACT_APP_DEFAULT_TEXT_COLOR }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Stack direction="row" spacing={5}>
                                                     <Typography
                                                        variant="caption"
                                                        maxWidth={400}
                                                        sx={{
                                                            whiteSpace: "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                        }}>
                                                        {`(${item.language}) `}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        maxWidth={400}
                                                        sx={{
                                                            whiteSpace: "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                        }}>
                                                        {item.fileName}
                                                    </Typography>
                                                </Stack>
                                            }
                                        />
                                    </ListItem>
                                )}
                            </List>
                        </Grid>
                    </Grid>}
                    
                    <Grid container columnSpacing={3} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <ZPlayAutocomplete
                                fieldName={`${this.props.name}.subtitlesLanguage`}
                                label={t({
                                    id: "lasd-subtitle-language",
                                    message: "Lingua sottotitoli"
                                })}
                                formState={this.props.formState}
                                control={this.props.control}
                                trigger={this.props.trigger}
                                data={this.state.languages}
                                disabledOption={this.state.languages.filter(language => {
                                    return this.props.subtitles.some(subtitle => subtitle.language === language.isoCode);
                                })}/>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl>
                                {!this.state.isSubtitlesUploaded && <Button
                                    component="label"
                                    variant="contained"
                                    startIcon={<UploadFileIcon />}
                                    sx={{
                                        background: `linear-gradient(279.75deg, #3E0449 -2.65%, #E50146 84.59%)`
                                    }}
                                    disabled={!this.props.isVideoUploaded}>
                                    <Trans id="lasd-upload-subtitle-file-btn">Upload file</Trans>
                                    <input hidden accept="pdf/*" multiple type="file" onChange={this.parseFileSubtitles} />
                                </Button>}
                                {this.state.isSubtitlesUploaded && <List>
                                    <ListItem
                                        secondaryAction={
                                            <IconButton edge="end" onClick={this.removeFileSubtitles}>
                                                <ClearIcon
                                                    fontSize="small"
                                                    sx={{ color: process.env.REACT_APP_DEFAULT_TEXT_COLOR }} />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemIcon>
                                            <UploadFileIcon
                                                fontSize="large"
                                                sx={{ color: process.env.REACT_APP_DEFAULT_TEXT_COLOR }}
                                            />
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary={this.state.fileSubtitlesName} 
                                            sx={{ color: process.env.REACT_APP_DEFAULT_TEXT_COLOR }}/>
                                    </ListItem>
                                </List>}
                            </FormControl>
                        </Grid>
                        <Grid item xs={2}>
                            <Tooltip title="Aggiungi i sottotitoli caricati" sx={{ float: "right" }}>
                                <Fab size="small" color="primary" onClick={this.addSubtitles} disabled={!this.props.isVideoUploaded}>
                                    <AddIcon />
                                </Fab>
                            </Tooltip>
                        </Grid>
                        <Grid item xs={12}>
                            {this.props.csvSubtitles !== "" && (
                                <Stack direction="row" spacing={1} mt={2}>
                                    <Typography
                                        variant={"body2"}
                                        color={process.env.REACT_APP_SECONDARY_TEXT_COLOR}>
                                        <Trans id="lasd-language-subtitles-csv-uploaded">
                                            I sottotitoli del contenuto sono: {this.props.csvSubtitles}
                                        </Trans>
                                    </Typography>
                                </Stack>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            {this.state.isSubtitlesUploaded && <Stack direction="row" spacing={1} mt={2}>
                                <WarningIcon fontSize="small" color="warning" />
                                <Typography
                                    variant={"body2"}
                                    color="warning.main">
                                    <Trans id={"lasd-language-subtitles-section-hint"}>Ricordati di inserire i sottotitoli caricati cliccando sull'apposito bottone a destra</Trans>
                                </Typography>
                            </Stack>}
                        </Grid>
                        <Grid item xs={12}>
                            <Stack direction="row" spacing={1} mt={1} mb={2}>
                                <HelpIcon fontSize="small" sx={{color: process.env.REACT_APP_SECONDARY_TEXT_COLOR}}/>
                                <Typography
                                    variant={"body2"}
                                    color={process.env.REACT_APP_SECONDARY_TEXT_COLOR}>
                                    <Trans id={"lasd-subtitle-explanation"}> Non puoi inserire sottotitoli prima di aver caricato il file video </Trans>
                                </Typography>
                            </Stack>
                        </Grid>
                    </Grid>
                    {this.state.error && <Stack direction="row" spacing={1} mt={2}>
                        <ErrorIcon fontSize="small" color="error" />
                        <Typography
                            variant={"body2"}
                            color="error">
                            <Trans id={"lasd-language-subtitles-section-error"}>Controlla di aver inserito la lingua e di aver caricato correttamente il file dei sottotitoli.</Trans>
                        </Typography>
                    </Stack>}
                </AccordionDetails>
            </Accordion>)
    }
}